<div class="pto-request-dialog"
  (dragenter)="onDialogDragEnter($event)"
  (dragover)="onDialogDragOver($event)"
  (dragleave)="onDialogDragLeave($event)"
  (drop)="onDialogDrop($event)">

  <!-- Whole-dialog drag-over overlay, shown while dragging files anywhere over the dialog -->
  @if (isDraggingOverDialog) {
    <div class="dialog-drag-overlay" role="region" aria-label="Drag and drop upload area">
      <div class="upload-indicator">
        @if (draggedFileCount > 0) {
          <div class="file-count-badge">{{draggedFileCount}} file{{draggedFileCount > 1 ? 's' : ''}}</div>
        }
        <div class="upload-icon-circle">
          <mat-icon>cloud_upload</mat-icon>
        </div>
        <p class="upload-text">Drop file{{draggedFileCount > 1 ? 's' : ''}} here to attach to this request</p>
      </div>
    </div>
  }

  <!-- Dialog Header -->
  <div class="dialog-header">
    <div class="header-icon">
      <mat-icon>beach_access</mat-icon>
    </div>
    <h2>Request Time Off</h2>
    <button class="close-btn" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Dialog Content -->
  <div class="dialog-content">
    <form [formGroup]="requestForm">
      <!-- PTO Type Selection -->
      <div class="form-group">
        <label class="form-label">
          <mat-icon>category</mat-icon>
          Time Off Type <span class="required">*</span>
        </label>
        <ng-select
          formControlName="request_type"
          [items]="ptoTypes"
          bindLabel="label"
          bindValue="value"
          placeholder="Select type of time off"
          [clearable]="false"
          [searchable]="false"
          (change)="onTypeChange($event)"
          class="pto-type-select">
          <ng-template ng-label-tmp let-item="item">
            <div class="type-option">
              <mat-icon [style.color]="item.color">{{item.icon}}</mat-icon>
              <span>{{item.label}}</span>
              <span class="available-badge" [style.background]="item.color">
                {{item.available}} available
              </span>
            </div>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            <div class="type-option">
              <mat-icon [style.color]="item.color">{{item.icon}}</mat-icon>
              <div class="type-details">
                <span class="type-name">{{item.label}}</span>
                <span class="type-balance">{{item.available}}/{{item.total}} days available</span>
              </div>
            </div>
          </ng-template>
        </ng-select>
        @if (requestForm.get('request_type')?.touched && requestForm.get('request_type')?.errors?.['required']) {
          <span class="error-text">
            Please select a time off type
          </span>
        }
      </div>

      <!-- Balance Summary Card (shown when type selected) -->
      @if (selectedType) {
        <div class="balance-summary" [ngClass]="selectedType.value.toLowerCase()">
          <div class="balance-header">
            <mat-icon [style.color]="selectedType.color">{{selectedType.icon}}</mat-icon>
            <span class="balance-title">{{selectedType.label}} Balance</span>
          </div>
          <div class="balance-progress">
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="getUsagePercentage()" [style.background]="selectedType.color"></div>
            </div>
            <div class="progress-labels">
              <span class="used">{{selectedType.total - selectedType.available}} used</span>
              <span class="available">{{selectedType.available}} available</span>
            </div>
          </div>
        </div>
      }

      <!-- Date Selection Row -->
      <div class="form-row date-row">
        <div class="form-group half">
          <label class="form-label">
            <mat-icon>event</mat-icon>
            Start Date <span class="required">*</span>
          </label>
          <mat-form-field appearance="outline" class="date-picker-field">
            <input matInput
              [matDatepicker]="startPicker"
              formControlName="start_date"
              [min]="minDate"
              placeholder="Select start date"
              (dateChange)="onDateChange()">
            <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>
          </mat-form-field>
          @if (requestForm.get('start_date')?.touched && requestForm.get('start_date')?.errors?.['required']) {
            <span class="error-text">
              Start date is required
            </span>
          }
        </div>

        <div class="form-group half">
          <label class="form-label">
            <mat-icon>event</mat-icon>
            End Date <span class="required">*</span>
          </label>
          <mat-form-field appearance="outline" class="date-picker-field">
            <input matInput
              [matDatepicker]="endPicker"
              formControlName="end_date"
              [min]="getMinEndDate()"
              placeholder="Select end date"
              (dateChange)="onDateChange()">
            <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>
          </mat-form-field>
          @if (requestForm.get('end_date')?.touched && requestForm.get('end_date')?.errors?.['required']) {
            <span class="error-text">
              End date is required
            </span>
          }
        </div>
      </div>

      <!-- Calculated Days Display -->
      @if (calculatedDays > 0) {
        <div class="days-display">
          <div class="days-info" [ngClass]="{'error': !validateDaysAgainstBalance()}">
            <mat-icon>schedule</mat-icon>
            <span class="days-count">{{calculatedDays}}</span>
            <span class="days-label">business day{{calculatedDays !== 1 ? 's' : ''}} requested</span>
          </div>
          @if (!validateDaysAgainstBalance()) {
            <span class="days-error">
              <mat-icon>warning</mat-icon>
              {{getDaysValidationError()}}
            </span>
          }
        </div>
      }

      <!-- Notes Field -->
      <div class="form-group">
        <label class="form-label">
          <mat-icon>notes</mat-icon>
          Notes <span class="optional">(Optional)</span>
        </label>
        <textarea
          formControlName="notes"
          class="notes-textarea"
          placeholder="Add any additional notes or reason for your request..."
          rows="3"
        maxlength="500"></textarea>
        <div class="char-count">
          {{requestForm.get('notes')?.value?.length || 0}}/500
        </div>
      </div>

      <!-- Documents Field -->
      <div class="form-group">
        <label class="form-label">
          <mat-icon>attach_file</mat-icon>
          Documents <span class="optional">(Optional)</span>
        </label>

        <div class="documents-drop-zone"
          [class.has-files]="stagedDocuments.length > 0"
          (click)="stagedDocuments.length === 0 ? docInput.click() : null">

          @if (stagedDocuments.length === 0) {
            <div class="documents-drop-zone-content">
              <mat-icon class="upload-icon">cloud_upload</mat-icon>
              <p class="drop-text">Drag & drop files here, or</p>
              <button mat-raised-button color="primary" type="button" class="browse-files-btn" (click)="$event.stopPropagation(); docInput.click()">
                <mat-icon>upload</mat-icon>
                Browse Files
              </button>
              <p class="file-types">PDF, image, Word or Excel &middot; max 10MB each</p>
            </div>
          }

          @if (stagedDocuments.length > 0) {
            <div class="document-list" (click)="$event.stopPropagation()">
              <div class="document-list-header">
                <span>{{stagedDocuments.length}} file{{stagedDocuments.length > 1 ? 's' : ''}} attached</span>
                <button mat-button type="button" (click)="$event.stopPropagation(); docInput.click()">
                  <mat-icon>add</mat-icon>
                  Add More
                </button>
              </div>
              <div class="document-items">
                @for (doc of stagedDocuments; track doc; let i = $index) {
                  <div class="document-item"
                    [class.uploading]="doc.status === 'uploading'"
                    [class.success]="doc.status === 'success'"
                    [class.error]="doc.status === 'error'">
                    <div class="document-preview" [class]="doc.preview ? '' : getDocumentTypeClass(doc.file.name)">
                      @if (doc.preview) {
                        <img [src]="doc.preview" [alt]="doc.file.name">
                      }
                      @if (!doc.preview) {
                        <mat-icon>{{getDocumentIcon(doc.file.name)}}</mat-icon>
                      }
                    </div>
                    <div class="document-info">
                      <div class="document-name">{{doc.file.name}}</div>
                      @if (doc.status === 'uploading') {
                        <div class="document-size">{{formatFileSize(doc.file.size)}}</div>
                        <div class="document-progress-bar">
                          <div class="document-progress-fill" [style.width.%]="doc.progress"></div>
                        </div>
                        <div class="document-status uploading">
                          <mat-icon>upload</mat-icon>
                          Uploading... {{doc.progress}}%
                        </div>
                      }
                      @if (doc.status === 'success') {
                        <div class="document-size">{{formatFileSize(doc.file.size)}}</div>
                        <div class="document-status success">
                          <mat-icon>check_circle</mat-icon>
                          Uploaded
                        </div>
                      }
                      @if (doc.status === 'error') {
                        <div class="document-status error">
                          <mat-icon>error</mat-icon>
                          {{doc.error || 'Failed'}}
                        </div>
                      }
                    </div>
                    <button mat-icon-button type="button" class="remove-document-btn"
                      (click)="$event.stopPropagation(); removeStagedDocument(i)"
                      matTooltip="Remove file">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </div>

        <input type="file" #docInput multiple [accept]="acceptedDocumentTypes"
          style="display: none" (change)="onDocumentInputChange($event)">
      </div>

      <!-- Request Summary (shown when form is complete) -->
      @if (showRequestSummary()) {
        <div class="request-summary">
          <div class="summary-header">
            <mat-icon>summarize</mat-icon>
            <span>Request Summary</span>
          </div>
          <div class="summary-content">
            <div class="summary-row">
              <span class="summary-label">Type</span>
              <span class="summary-value">
                <mat-icon [style.color]="selectedType?.color">{{selectedType?.icon}}</mat-icon>
                {{selectedType?.label}}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Dates</span>
              <span class="summary-value">
                {{formatDate(requestForm.get('start_date')?.value)}} - {{formatDate(requestForm.get('end_date')?.value)}}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Days</span>
              <span class="summary-value highlight">{{calculatedDays}} day{{calculatedDays !== 1 ? 's' : ''}}</span>
            </div>
            @if (selectedType) {
              <div class="summary-row">
                <span class="summary-label">Remaining After</span>
                <span class="summary-value" [ngClass]="{'warning': getRemainingBalance() < 3}">
                  {{getRemainingBalance()}} day{{getRemainingBalance() !== 1 ? 's' : ''}}
                </span>
              </div>
            }
          </div>
        </div>
      }
    </form>
  </div>

  <!-- Dialog Footer -->
  <div class="dialog-footer">
    <button class="btn-cancel" (click)="close()">
      Cancel
    </button>
    <button class="btn-submit"
      (click)="onSubmit()"
      [disabled]="!isFormValid() || isSubmitting">
      @if (!isSubmitting) {
        <mat-icon>send</mat-icon>
      }
      @if (isSubmitting) {
        <mat-spinner diameter="20"></mat-spinner>
      }
      {{isSubmitting ? 'Submitting...' : 'Submit Request'}}
    </button>
  </div>
</div>
